/**
 * What an instrument calls itself — "Order Schedule No. 5", "Third
 * Amendment", "Amended and Restated Master Agreement" — read out of a
 * contract's title or a file's name.
 *
 * Words are matched with no letter either side of them rather than with `\b`:
 * an underscore separates words in a file name as a space does
 * (`SP_OS25_Signed.pdf`), and `\b` would count it as part of the word. A name
 * run together in CamelCase is split into its words first.
 */

/** "OrderScheduleNumber27" → "Order Schedule Number27". */
const inWords = (name: string) => name.replace(/([a-z])(?=[A-Z])/g, '$1 ');

/** A word or phrase, standing on its own. */
const word = (pattern: string) =>
  new RegExp(`(?:^|[^a-z])(?:${pattern})(?![a-z])`, 'i');

/** A word or phrase and the figures after it: "No. 5", "#5", "_5", "5". */
const numbered = (pattern: string) =>
  new RegExp(
    `(?:^|[^a-z])(?:${pattern})[\\s_.-]*(?:number|no\\.?|#)?[\\s_-]*(\\d+)`,
    'i'
  );

const ORDER_SCHEDULE = word('order[\\s_]+schedule');
const NUMBERED_SCHEDULE = numbered('order[\\s_]+schedule|os');
/* "Schedule No. 8" without "Order" counts only with "No.", "Number" or "#",
   so neither "Fee Schedule 2024" nor a master's "Schedule 1" exhibit does. */
const SCHEDULE_NO = /(?:^|[^a-z])schedule[\s_-]*(?:number|no\.?|#)[\s_-]*\d/i;
/* Only for a row already known to be a schedule: then "Schedule 8" is OS8. */
const SCHEDULE_NUMBER = numbered('(?:order[\\s_]+)?schedule|os');

const MASTER = word('master');

/* The words the Contract Register also files as amendments (`kindOfType`). */
const AMENDMENT_WORDS = 'amendment|addendum|variation';
const AMENDMENT = word(AMENDMENT_WORDS);
/* An abbreviation counts only with its number: "Amd3", "Amdt No 4". */
const AMENDMENT_NUMBER = numbered(`${AMENDMENT_WORDS}|amdt|amd|amend`);

const RESTATEMENT = word(
  [
    'amended[\\s_]*(?:&|and)[\\s_]*restated',
    'amendment[\\s_]+and[\\s_]+restatement',
    'a[\\s_]*&[\\s_]*r',
  ].join('|')
);

/* Amendments are often numbered in words: "Third Amendment", "Amendment
   Number Three". Each list is in order, so a word's place is its number. */
// prettier-ignore
const ORDINALS = [
  'first', 'second', 'third', 'fourth', 'fifth',
  'sixth', 'seventh', 'eighth', 'ninth', 'tenth',
  'eleventh', 'twelfth', 'thirteenth', 'fourteenth', 'fifteenth',
  'sixteenth', 'seventeenth', 'eighteenth', 'nineteenth', 'twentieth',
];
// prettier-ignore
const CARDINALS = [
  'one', 'two', 'three', 'four', 'five',
  'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen',
  'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty',
];
const ORDINAL_WORDS = ORDINALS.join('|');
const CARDINAL_WORDS = CARDINALS.join('|');

/* "Third Amendment" */
const ORDINAL_AMENDMENT = new RegExp(
  `(?:^|[^a-z])(${ORDINAL_WORDS})[\\s_-]+(?:${AMENDMENT_WORDS})`,
  'i'
);
/* "Amendment Number Three" */
const AMENDMENT_IN_WORDS = new RegExp(
  `(?:^|[^a-z])(?:${AMENDMENT_WORDS})[\\s_-]*(?:number|no\\.?)?[\\s_-]*(${CARDINAL_WORDS})(?![a-z])`,
  'i'
);

/** "Amended and Restated …", "Amendment and Restatement of …", "… (A&R)". */
export const namesRestatement = (name: string): boolean =>
  RESTATEMENT.test(inWords(name));

/** "Amendment", "Addendum", "Variation", or a numbered "Amd3" / "Amdt No 4". */
export const namesAmendment = (name: string): boolean => {
  const words = inWords(name);
  return AMENDMENT.test(words) || AMENDMENT_NUMBER.test(words);
};

export const namesMaster = (name: string): boolean =>
  MASTER.test(inWords(name));

/** "Order Schedule No. 5", "OS5", "SP_OS25". */
export const namesNumberedSchedule = (name: string): boolean =>
  NUMBERED_SCHEDULE.test(inWords(name));

/** "Order Schedule", numbered or not, or "Schedule No. 8". */
export const namesOrderSchedule = (name: string): boolean => {
  const words = inWords(name);
  return ORDER_SCHEDULE.test(words) || SCHEDULE_NO.test(words);
};

/** A schedule's number, if its name gives one: "Schedule No. 8" → 8. */
export const scheduleNumberIn = (name: string): number | null => {
  const figures = SCHEDULE_NUMBER.exec(inWords(name))?.[1];
  return figures ? Number(figures) : null;
};

/* The amendments a consolidated version takes in, listed after the word:
   "with amendments 1, 2, and 3", "… 1, 2, 3, 4, 5, to 26", "Amendments 1-26",
   "AMENDMENT NO. 1". A hyphen counts only between figures, so a year after a
   dash ("Amendment 3 - 2019") is not read as part of the list. */
const AMENDMENT_LIST = new RegExp(
  `(?:^|[^a-z])(?:${AMENDMENT_WORDS}|amdt|amd|amend)s?((?:\\d+(?:[–-]\\d+)?|[\\s_.,&#]|and|to|through|no|number)*)`,
  'i'
);

/**
 * The last amendment a consolidated version's name says it takes in: "(with
 * amendments 1, 2, and 3)" is 3, "(with amendments 1, 2, 3, 4, 5, to 26)" 26.
 */
export const latestAmendmentIn = (name: string): number | null => {
  const list = AMENDMENT_LIST.exec(inWords(name))?.[1] ?? '';
  const figures = list.match(/\d+/g)?.map(Number);
  return figures?.length ? Math.max(...figures) : null;
};

/**
 * An amendment's number, if its name gives one, in figures or in words:
 * "Amendment 3", "Amd3", "Addendum No. 3", "Third Amendment" and "Amendment
 * Number Three" are all 3.
 */
export const amendmentNumberIn = (name: string): number | null => {
  const words = inWords(name);
  const figures = AMENDMENT_NUMBER.exec(words)?.[1];
  if (figures) return Number(figures);

  const ordinal = ORDINAL_AMENDMENT.exec(words)?.[1];
  if (ordinal) return ORDINALS.indexOf(ordinal.toLowerCase()) + 1;

  const cardinal = AMENDMENT_IN_WORDS.exec(words)?.[1];
  if (cardinal) return CARDINALS.indexOf(cardinal.toLowerCase()) + 1;

  return null;
};
